import React, { useState, useEffect } from 'react';

const AutoSellOptionsModal = ({ symbol, stockName, onClose, authenticatedFetch, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [globalAutoSell, setGlobalAutoSell] = useState(null);
  const [symbolConfig, setSymbolConfig] = useState(null);
  
  // 모달이 열릴 때 설정 조회
  useEffect(() => {
    fetchConfig();
  }, [symbol]);
  
  const fetchConfig = async () => {
    setLoading(true);
    try {
      // 전체 AI 거래 설정 조회
      const response = await authenticatedFetch('/api/ai-trade/config');
      if (response.ok) {
        const data = await response.json();
        const configData = data.config;
        setConfig(configData);
        
        // 전역 자동매도 설정
        setGlobalAutoSell({
          enabled: configData.auto_sell?.enabled ?? configData.metadata?.auto_sell?.enabled ?? true,
          confidence_threshold: configData.auto_sell?.confidence_threshold ?? configData.metadata?.auto_sell?.confidence_threshold ?? 70,
          quantity_percent: configData.auto_sell?.quantity_percent ?? configData.metadata?.auto_sell?.quantity_percent ?? 50,
          max_price_change_percent: configData.auto_sell?.max_price_change_percent ?? configData.metadata?.auto_sell?.max_price_change_percent ?? 10,
          adjust_price_on_volatility: configData.auto_sell?.adjust_price_on_volatility ?? configData.metadata?.auto_sell?.adjust_price_on_volatility ?? true
        });
        
        // 종목별 설정 확인
        const symbolConfigItem = configData.symbol_configs?.find(sc => sc.symbol === symbol);
        if (symbolConfigItem) {
          setSymbolConfig(symbolConfigItem);
        }
      }
    } catch (error) {
      console.error('설정 조회 실패:', error);
      alert('설정 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    // 종목별 설정 저장은 필요시 구현
    // 현재는 조회만 제공
    if (onSave) {
      await onSave();
    }
    onClose();
  };
  
  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px'
        }}>
          설정을 불러오는 중...
        </div>
      </div>
    );
  }
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #e0e0e0',
          paddingBottom: '12px'
        }}>
          <h2 style={{ margin: 0, color: '#333' }}>
            🤖 AI 자동매도 옵션
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#999',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '8px' }}>
            {symbol} - {stockName}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            종목별 AI 자동매도 설정 정보
          </div>
        </div>
        
        {/* 전역 자동매도 설정 */}
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#495057' }}>
            전역 자동매도 설정
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* 자동매도 활성화 체크박스 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={globalAutoSell?.enabled ?? true}
                  disabled
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                />
                <span style={{ fontWeight: '500', fontSize: '15px' }}>✅ 자동매도 활성화</span>
              </label>
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: (globalAutoSell?.enabled ?? true) ? '#d4edda' : '#f8d7da',
                color: (globalAutoSell?.enabled ?? true) ? '#155724' : '#721c24',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                {(globalAutoSell?.enabled ?? true) ? '활성화' : '비활성화'}
              </span>
            </div>
            
            {/* 확신도 임계값 체크박스 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                />
                <span style={{ fontWeight: '500', fontSize: '14px' }}>확신도 임계값: {globalAutoSell?.confidence_threshold ?? 70}%</span>
              </label>
              <span style={{ fontSize: '12px', color: '#666' }}>
                AI 확신도 {globalAutoSell?.confidence_threshold ?? 70}% 이상일 때만 매도
              </span>
            </div>
            
            {/* 매도 수량 비율 체크박스 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                />
                <span style={{ fontWeight: '500', fontSize: '14px' }}>매도 수량 비율: {globalAutoSell?.quantity_percent ?? 50}%</span>
              </label>
              <span style={{ fontSize: '12px', color: '#666' }}>
                보유 수량의 {globalAutoSell?.quantity_percent ?? 50}% 매도
              </span>
            </div>
            
            {/* 가격 변동률 임계값 체크박스 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                />
                <span style={{ fontWeight: '500', fontSize: '14px' }}>가격 변동률 임계값: {globalAutoSell?.max_price_change_percent ?? 10}%</span>
              </label>
              <span style={{ fontSize: '12px', color: '#666' }}>
                가격 변동률 {globalAutoSell?.max_price_change_percent ?? 10}% 초과 시 주문 조정
              </span>
            </div>
            
            {/* 가격 변동 시 조정 체크박스 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                <input
                  type="checkbox"
                  checked={globalAutoSell?.adjust_price_on_volatility ?? true}
                  disabled
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                />
                <span style={{ fontWeight: '500', fontSize: '14px' }}>가격 변동 시 조정</span>
              </label>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {globalAutoSell?.adjust_price_on_volatility ? '가격 변동률 초과 시 현재가로 주문가 조정' : '가격 변동률 초과 시 주문 취소'}
              </span>
            </div>
          </div>
        </div>
        
        {/* 종목별 설정 */}
        {symbolConfig ? (
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#e7f3ff',
            borderRadius: '6px',
            border: '1px solid #b3d9ff'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#004085' }}>
              종목별 AI 추천 설정
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* AI 추천 액션 체크박스 */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                  <input
                    type="checkbox"
                    checked={symbolConfig.ai_recommendation?.action === 'sell'}
                    disabled
                    style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                  />
                  <span style={{ fontWeight: '500', fontSize: '14px' }}>
                    AI 추천 액션: {symbolConfig.ai_recommendation?.action || 'none'}
                  </span>
                </label>
                <span style={{ fontSize: '12px', color: '#666' }}>
                  ({symbolConfig.ai_recommendation?.confidence || 0}% 확신도)
                </span>
              </div>
              
              {/* 목표가 체크박스 */}
              {symbolConfig.ai_recommendation?.target_price > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                    />
                    <span style={{ fontWeight: '500', fontSize: '14px' }}>
                      목표가: ₩{symbolConfig.ai_recommendation.target_price.toLocaleString()}
                    </span>
                  </label>
                </div>
              )}
              
              {/* 손절가 체크박스 */}
              {symbolConfig.ai_recommendation?.stop_loss_price > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: 'white', borderRadius: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                    />
                    <span style={{ fontWeight: '500', fontSize: '14px', color: '#dc3545' }}>
                      손절가: ₩{symbolConfig.ai_recommendation.stop_loss_price.toLocaleString()}
                    </span>
                  </label>
                </div>
              )}
              
              {/* 예약 주문 체크박스 */}
              {symbolConfig.scheduled_orders && symbolConfig.scheduled_orders.length > 0 && (
                <div style={{ marginTop: '4px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '14px' }}>예약 주문:</div>
                  {symbolConfig.scheduled_orders.map((order, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      padding: '8px', 
                      backgroundColor: 'white', 
                      borderRadius: '4px',
                      marginBottom: '4px'
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'not-allowed', flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={order.order_type === 'sell'}
                          disabled
                          style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'not-allowed' }}
                        />
                        <span style={{ fontSize: '13px' }}>
                          {order.order_type === 'sell' ? '매도' : '매수'} {order.quantity}주 @ ₩{order.price.toLocaleString()}
                        </span>
                      </label>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {new Date(order.scheduled_time).toLocaleString('ko-KR')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* 마지막 분석 시간 */}
              {symbolConfig.last_analysis_time && (
                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: 'white', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
                  마지막 분석: {new Date(symbolConfig.last_analysis_time).toLocaleString('ko-KR')}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#fff3cd',
            borderRadius: '6px',
            border: '1px solid #ffc107',
            textAlign: 'center',
            color: '#856404'
          }}>
            이 종목에 대한 종목별 설정이 없습니다.
            <br />
            <span style={{ fontSize: '12px' }}>전역 자동매도 설정이 적용됩니다.</span>
          </div>
        )}
        
        {/* AI 거래 활성화 상태 */}
        {config && (
          <div style={{
            marginBottom: '24px',
            padding: '12px',
            backgroundColor: config.is_active ? '#d4edda' : '#f8d7da',
            borderRadius: '6px',
            border: `1px solid ${config.is_active ? '#c3e6cb' : '#f5c6cb'}`,
            textAlign: 'center'
          }}>
            <strong style={{ color: config.is_active ? '#155724' : '#721c24' }}>
              AI 거래 상태: {config.is_active ? '✅ 활성화' : '❌ 비활성화'}
            </strong>
          </div>
        )}
        
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            닫기
          </button>
          <button
            onClick={() => {
              // AI 거래 설정 모달로 이동하는 기능 추가 가능
              alert('AI 거래 설정을 변경하려면 상단의 "🤖 AI거래설정" 탭에서 설정하세요.');
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            설정 변경
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoSellOptionsModal;


